import { useState, useCallback, useEffect } from 'react';
import { v4 as uuidv4 } from "uuid";
import {
    IContractorMotivationEditDTO,
    IIlliquidGoodsStoresEditDTO,
    IIsgGoodsStoresEditDTO,
    IMotivationBonusEditDTO,
    IMotivationGoodsGroupEditDTO,
    IMotivationStoreEditDTO
} from '../../../../../libs/coreapi-dto/dirs/contractorsMotivation';
import { IIsgGoodsDTO, IIlliquidGoodsDTO } from '../../../../../libs/coreapi-dto/dirs/contractorsMotivation';
import { DateTime } from 'luxon';

const getDefaultMotivation = (idContractorGlobal?: string) => {
    const idGlobal = uuidv4()
    const defaultMotivation: IContractorMotivationEditDTO =
    {
        idGlobal: idGlobal,
        dateCreated: DateTime.now(),
        dateModified: DateTime.now(),
        dateDeleted: null,
        idContractorGlobal: idContractorGlobal || '',
        bonusMzActive: false,
        bonusMz: 0,
        kppm: 1,
        skipProfitCheckType: 0,
        motivationPercent: 0,
        marginCode: 0,
        motivationActiveInternetOrder: false,
        periodReglBuildInternetOrder: 0,
        sumPenaltyInternetOrder: 0,
        sumPremiumInternetOrder: 0,
        motivationActiveAddSaleInternetOrder: false,
        bonusAddSaleInternetOrder: 0,
        bonusUnitAddSaleInternetOrder: 0,
        motivationActiveIsgGoods: false,
        isgGoods: [],
        motivationActiveIlliquidGoods: false,
        illiquidGoods: [],
        motivationActiveIlliquidGoodsStores: false,
        illiquidGoodsStores: [],
        motivationActiveIsgGoodsStores: false,
        isgGoodsStores: [],
        bonuses: [
            {
                idGlobal: uuidv4(),
                dateCreated: DateTime.now(),
                dateModified: DateTime.now(),
                dateDeleted: null,
                idContractorMotivationGlobal: idGlobal,
                value: 0,
                marginCode: 0,
                enabledStores: false,
                stores: [],
                enabledGoodsGroups: false,
                goodsGroups: []
            }
        ]
    }
    return defaultMotivation
};

export const useContractorMotivation = (
    initialMotivation?: IContractorMotivationEditDTO,
    contractorId?: string
) => {
    const [motivation, setMotivation] = useState<IContractorMotivationEditDTO>(
        initialMotivation || getDefaultMotivation(contractorId)
    );

    useEffect(() => {
        if (initialMotivation) {
            setMotivation(initialMotivation);
        }
    }, [initialMotivation]);

    const updateField = useCallback(<K extends keyof IContractorMotivationEditDTO>(
        field: K,
        value: IContractorMotivationEditDTO[K]
    ) => {
        setMotivation(prev => ({ ...prev, [field]: value }));
    }, []);

    const updateBonusMzActive = (value: boolean) => updateField('bonusMzActive', value);
    const updateBonusMz = (value: number) => updateField('bonusMz', value);
    const updateKppm = (value: number) => updateField('kppm', value);
    const updateSkipProfitCheckType = (value: number) => updateField('skipProfitCheckType', value);
    const updateMotivationPercent = (value: number) => updateField('motivationPercent', value);
    const updateMarginCode = (value: number) => updateField('marginCode', value);
    const updateMotivationActiveInternetOrder = (value: boolean) => updateField('motivationActiveInternetOrder', value);
    const updatePeriodReglBuildInternetOrder = (value: number) => updateField('periodReglBuildInternetOrder', value);
    const updateSumPenaltyInternetOrder = (value: number) => updateField('sumPenaltyInternetOrder', value);
    const updateSumPremiumInternetOrder = (value: number) => updateField('sumPremiumInternetOrder', value);
    const updateMotivationActiveAddSaleInternetOrder = (value: boolean) => updateField('motivationActiveAddSaleInternetOrder', value);
    const updateBonusAddSaleInternetOrder = (value: number) => updateField('bonusAddSaleInternetOrder', value);
    const updateBonusUnitAddSaleInternetOrder = (value: number) => updateField('bonusUnitAddSaleInternetOrder', value);
    const updateMotivationActiveIsgGoods = (value: boolean) => updateField('motivationActiveIsgGoods', value);
    const updateMotivationActiveIsgGoodsStores = (value: boolean) => updateField('motivationActiveIsgGoodsStores', value);
    const updateMotivationActiveIlliquidGoodsStores = (value: boolean) => updateField('motivationActiveIlliquidGoodsStores', value);
    const updateMotivationActiveIlliquidGoods = (value: boolean) => updateField('motivationActiveIlliquidGoods', value);

    const setIsgGoods = useCallback((isgGoods: IIsgGoodsDTO[]) => {
        setMotivation(prev => ({ ...prev, isgGoods }));
    }, []);

    const setIlliquidGoods = useCallback((illiquidGoods: IIlliquidGoodsDTO[]) => {
        setMotivation(prev => ({ ...prev, illiquidGoods }));
    }, []);

    const updateBonusStores = useCallback((
        bonusIndex: number,
        stores: IMotivationStoreEditDTO[]
    ) => {
        setMotivation(prev => {
            if (!prev || !prev.bonuses[bonusIndex]) return prev;

            const updatedBonuses = [...prev.bonuses];
            updatedBonuses[bonusIndex] = {
                ...updatedBonuses[bonusIndex],
                stores: stores
            };

            return {
                ...prev,
                bonuses: updatedBonuses
            };
        });
    }, []);
    
    const updateIlliquidGoodsStores = useCallback((
        stores: IIlliquidGoodsStoresEditDTO[]
    ) => {
        setMotivation(prev => {
            if (!prev) return prev;
            return {
                ...prev,
                illiquidGoodsStores: stores
            };
        });
    }, []);
    
    const updateIsgGoodsStores = useCallback((
        stores: IIsgGoodsStoresEditDTO[]
    ) => {
        setMotivation(prev => {
            if (!prev) return prev;
            return {
                ...prev,
                isgGoodsStores: stores
            };
        });
    }, []);

    const updateBonusGoodsGroups = useCallback((
        bonusIndex: number,
        goodsGroups: IMotivationGoodsGroupEditDTO[]
    ) => {
        setMotivation(prev => {
            if (!prev || !prev.bonuses[bonusIndex]) return prev;

            const updatedBonuses = [...prev.bonuses];
            updatedBonuses[bonusIndex] = {
                ...updatedBonuses[bonusIndex],
                goodsGroups: goodsGroups
            };

            return {
                ...prev,
                bonuses: updatedBonuses
            };
        });
    }, []);

    const addBonusToMotivation = useCallback((newBonus: IMotivationBonusEditDTO) => {
        setMotivation(prev => {
            if (!prev) return prev;
            
            return {
                ...prev,
                bonuses: [...prev.bonuses, newBonus]
            };
        });
    }, []);

    const removeBonus = useCallback((index: number) => {
        setMotivation(prev => ({
            ...prev,
            bonuses: prev.bonuses.filter((_, i) => i !== index)
        }));
    }, []);

    const updateBonusField = useCallback(<K extends keyof IMotivationBonusEditDTO>(
        bonusIndex: number,
        field: K,
        value: IMotivationBonusEditDTO[K]
    ) => {
        setMotivation(prev => {
            const updatedBonuses = [...prev.bonuses];
            updatedBonuses[bonusIndex] = { ...updatedBonuses[bonusIndex], [field]: value };
            return { ...prev, bonuses: updatedBonuses };
        });
    }, []);

    const resetToDefault = useCallback(() => {
        setMotivation(getDefaultMotivation(contractorId));
    }, [contractorId]);

    const setMotivationData = useCallback((newMotivation: IContractorMotivationEditDTO) => {
        setMotivation(newMotivation);
    }, []);

    return {
        motivation,
        setMotivationData,
        resetToDefault,
        updateBonusMzActive,
        updateBonusMz,
        updateKppm,
        updateIsgGoodsStores,
        updateIlliquidGoodsStores,
        updateSkipProfitCheckType,
        updateMotivationPercent,
        updateMarginCode,
        updateMotivationActiveInternetOrder,
        updatePeriodReglBuildInternetOrder,
        updateSumPenaltyInternetOrder,
        updateSumPremiumInternetOrder,
        updateMotivationActiveAddSaleInternetOrder,
        updateBonusAddSaleInternetOrder,
        updateBonusUnitAddSaleInternetOrder,
        updateMotivationActiveIsgGoods,
        updateMotivationActiveIlliquidGoods,
        updateMotivationActiveIsgGoodsStores,
        updateMotivationActiveIlliquidGoodsStores,
        setIsgGoods,
        setIlliquidGoods,
        addBonusToMotivation,
        removeBonus,
        updateBonusStores,
        updateBonusGoodsGroups,
        updateBonusField
    };
};